import React, { useCallback, useEffect } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import { useVersion } from 'react-admin';
import { Box, Button, CircularProgress } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import EditIcon from '@material-ui/icons/Edit';

import { PermissionsProvider } from 'providers/permissionsProvider';
import {
  CAN_EDIT_MODEL,
  CAN_EDIT_SP_POTENTIAL_MATCHES,
  CAN_SEE_SP_POTENTIAL_MATCHES,
} from 'providers/permissions';
import { SnackbarContainer } from 'components/standalone';

import { BuilderContainer } from '../containers';
import { BuilderForm, SpMatchesSection } from '../components';

const BuildersShowInner: React.FC = () => {
  const classes = useStyles();
  const history = useHistory();
  const { id } = useParams<{ id: string }>();
  const { notify } = SnackbarContainer.useContainer();
  const { hasPermission } = PermissionsProvider.useContainer();

  // The top-toolbar Edit button is deliberately gated on CAN_EDIT_MODEL,
  // not CAN_EDIT_BUILDER.
  const canEdit = hasPermission(CAN_EDIT_MODEL);
  const canSeeSpMatches = hasPermission(CAN_SEE_SP_POTENTIAL_MATCHES);
  const canEditSpMatches = hasPermission(CAN_EDIT_SP_POTENTIAL_MATCHES);

  const {
    selectedBuilder,
    isLoading,
    builderLoaded,
    builderError,
    loadBuilderById,
    loadSpBuilders,
  } = BuilderContainer.useContainer();

  // BACKWARDS COMPAT: useVersion triggers re-fetch when react-admin
  // toolbar refresh button is clicked. Required while using custom
  // containers instead of react-admin's built-in data hooks.
  const version = useVersion();

  const builderId = selectedBuilder?.id;

  useEffect(() => {
    if (!id) return;
    loadBuilderById(id);
  }, [id, version, loadBuilderById]);

  useEffect(() => {
    if (builderError || (builderLoaded && !selectedBuilder)) {
      notify('Element does not exist', 'error');
      history.push('/builder');
    }
  }, [builderError, builderLoaded, selectedBuilder, notify, history]);

  // Two-tier gating: the outer section is visible on
  // CAN_SEE_SP_POTENTIAL_MATCHES, the inner edit affordances on
  // CAN_EDIT_SP_POTENTIAL_MATCHES. The asymmetry with the Edit page's
  // EDIT-only gating is deliberate.
  useEffect(() => {
    if (!builderId || !canSeeSpMatches) return;
    loadSpBuilders(builderId).catch(() => {
      notify('Failed to load SupplyPro builder names.', 'error');
    });
  }, [builderId, canSeeSpMatches, loadSpBuilders, notify]);

  const handleEdit = useCallback(() => {
    history.push(`/builder/${id}`);
  }, [history, id]);

  const handleGoBack = useCallback(() => {
    history.push('/builder');
  }, [history]);

  if (isLoading) {
    return (
      <Box className={classes.loadingContainer}>
        <CircularProgress />
      </Box>
    );
  }

  if (!selectedBuilder) {
    return null;
  }

  return (
    <Box className={classes.root}>
      <Box className={classes.topToolbar}>
        {canEdit && (
          <Button
            color="primary"
            startIcon={<EditIcon />}
            onClick={handleEdit}
            size="small"
          >
            Edit
          </Button>
        )}
      </Box>

      <Box className={classes.formContainer}>
        <BuilderForm
          mode="show"
          initialValues={selectedBuilder}
          loading={false}
          onGoBack={handleGoBack}
        />
      </Box>

      {canSeeSpMatches && <SpMatchesSection canEdit={canEditSpMatches} />}
    </Box>
  );
};

export const BuildersShow: React.FC = () => (
  <BuilderContainer.Provider>
    <BuildersShowInner />
  </BuilderContainer.Provider>
);

const useStyles = makeStyles(theme => ({
  root: {
    maxWidth: '1500px',
  },
  topToolbar: {
    display: 'flex',
    justifyContent: 'flex-end',
    alignItems: 'center',
    marginBottom: theme.spacing(1),
    minHeight: 'auto',
  },
  formContainer: {
    position: 'relative',
    boxShadow: theme.shadows[2],
    backgroundColor: theme.palette.background.paper,
  },
  loadingContainer: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    padding: theme.spacing(8),
  },
}));

export default BuildersShow;
